import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Container,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
  RadialLinearScale,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";
import {
  TrendingUp,
  TrendingDown,
  AttachMoney as DollarSign,
  CallReceived as Outflow,
  Assessment,
} from "@mui/icons-material";
import Layout from "../../components/layout/ChartsLayout";
import config from "../../config";


// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
  RadialLinearScale
);

const theme = createTheme({
  palette: {
    primary: {
      main: "#EB5A3C",
      light: "#FF7A5C",
      dark: "#C54428",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#F8EEDB",
      light: "#FFFAF0",
      dark: "#E6D4B7",
      contrastText: "#333333",
    },
    background: {
      default: "#F8EEDB",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#333333",
      secondary: "#666666",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: "0 4px 20px rgba(235, 90, 60, 0.1)",
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 8px 30px rgba(235, 90, 60, 0.15)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
  },
});

const Profit_Loss = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { API_BASE_URL } = config;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${API_BASE_URL}/api/v1/getFinancialReports`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          setData(result);
          setError(null);
        } else {
          throw new Error(result.message || "Failed to fetch data");
        }
      } catch (err) {
        console.error("Error fetching financial data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API_BASE_URL]);

  const excelDateToJS = (serial) => {
    if (!serial) return "N/A";
    const excelStartDate = new Date(1900, 0, 1);
    const jsDate = new Date(
      excelStartDate.getTime() + (serial - 2) * 24 * 60 * 60 * 1000
    );
    return jsDate.toLocaleDateString();
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "$0";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return "0%";
    return `${(value * 100).toFixed(1)}%`;
  };

  if (loading) {
    return (
      <Layout>
        <ThemeProvider theme={theme}>
          <Box
            sx={{
              minHeight: "100vh",
              background: "linear-gradient(135deg, #F8EEDB 0%, #FFFAF0 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  textAlign: "center",
                  borderRadius: 4,
                }}
              >
                <CircularProgress size={60} sx={{ color: "#EB5A3C", mb: 2 }} />
                <Typography variant="h6" color="text.primary">
                  Loading Profit & Loss Data...
                </Typography>
              </Paper>
            </motion.div>
          </Box>
        </ThemeProvider>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <ThemeProvider theme={theme}>
          <Box
            sx={{
              minHeight: "100vh",
              background: "linear-gradient(135deg, #F8EEDB 0%, #FFFAF0 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Alert
                severity="error"
                sx={{
                  maxWidth: 600,
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Error loading profit & loss data
                </Typography>
                <Typography variant="body2">{error}</Typography>
              </Alert>
            </motion.div>
          </Box>
        </ThemeProvider>
      </Layout>
    );
  }

  if (!data?.success || !data?.data?.reports?.Profit_Loss) {
    return (
      <Layout>
        <ThemeProvider theme={theme}>
          <Box
            sx={{
              minHeight: "100vh",
              background: "linear-gradient(135deg, #F8EEDB 0%, #FFFAF0 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Alert severity="warning">No profit & loss data available</Alert>
          </Box>
        </ThemeProvider>
      </Layout>
    );
  }

  const profitLossData = data.data.reports.Profit_Loss?.data?.Sheet1 || [];

  // Calculate summary metrics
  const totalRevenue = profitLossData.reduce(
    (sum, item) => sum + (item.Revenue || 0),
    0
  );
  const totalExpenses = profitLossData.reduce(
    (sum, item) => sum + (item.COGS || 0) + (item["Operating Expenses"] || 0),
    0
  );
  const totalNetIncome = profitLossData.reduce(
    (sum, item) => sum + (item["Net Income"] || 0),
    0
  );
  const averageGrossMargin =
    profitLossData.length > 0
      ? profitLossData.reduce((sum, item) => {
          const revenue = item.Revenue || 0;
          const grossProfit = item["Gross Profit"] || 0;
          return sum + (revenue > 0 ? grossProfit / revenue : 0);
        }, 0) / profitLossData.length
      : 0;

  // Prepare chart data
  const chartLabels = profitLossData.map((item) => excelDateToJS(item.Date));
  const revenueData = profitLossData.map((item) => item.Revenue || 0);
  const netIncomeData = profitLossData.map((item) => item["Net Income"] || 0);
  const grossProfitData = profitLossData.map(
    (item) => item["Gross Profit"] || 0
  );

  const lineChartData = {
    labels: chartLabels,
    datasets: [
      {
        label: "Revenue",
        data: revenueData,
        borderColor: "#EB5A3C",
        backgroundColor: "rgba(235, 90, 60, 0.1)",
        tension: 0.4,
        fill: true,
      },
      {
        label: "Net Income",
        data: netIncomeData,
        borderColor: "#28a745",
        backgroundColor: "rgba(40, 167, 69, 0.1)",
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const barChartData = {
    labels: chartLabels.slice(-12), // Last 12 periods
    datasets: [
      {
        label: "Revenue",
        data: revenueData.slice(-12),
        backgroundColor: "rgba(235, 90, 60, 0.8)",
      },
      {
        label: "Gross Profit",
        data: grossProfitData.slice(-12),
        backgroundColor: "rgba(40, 167, 69, 0.8)",
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return (
              context.dataset.label + ": " + formatCurrency(context.parsed.y)
            );
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return formatCurrency(value);
          },
        },
      },
    },
  };

  return (
    <Layout>
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            minHeight: "100vh",
            background: "linear-gradient(135deg, #F8EEDB 0%, #FFFAF0 100%)",
            py: 3,
          }}
        >
          <Container maxWidth="xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Typography
                variant="h4"
                gutterBottom
                sx={{ color: "text.primary", mb: 4, textAlign: "center" }}
              >
                Profit & Loss Statement
              </Typography>

              {/* Summary Cards */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <Avatar sx={{ bgcolor: "primary.main", mr: 2 }}>
                          <DollarSign />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" color="text.primary">
                            Total Revenue
                          </Typography>
                          <Typography variant="h5" color="primary.main">
                            {formatCurrency(totalRevenue)}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <Avatar sx={{ bgcolor: "error.main", mr: 2 }}>
                          <Outflow />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" color="text.primary">
                            Total Expenses
                          </Typography>
                          <Typography variant="h5" color="error.main">
                            {formatCurrency(totalExpenses)}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <Avatar
                          sx={{
                            bgcolor:
                              totalNetIncome >= 0
                                ? "success.main"
                                : "error.main",
                            mr: 2,
                          }}
                        >
                          {totalNetIncome >= 0 ? (
                            <TrendingUp />
                          ) : (
                            <TrendingDown />
                          )}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" color="text.primary">
                            Net Income
                          </Typography>
                          <Typography
                            variant="h5"
                            color={
                              totalNetIncome >= 0
                                ? "success.main"
                                : "error.main"
                            }
                          >
                            {formatCurrency(totalNetIncome)}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <Avatar sx={{ bgcolor: "info.main", mr: 2 }}>
                          <Assessment />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" color="text.primary">
                            Avg Gross Margin
                          </Typography>
                          <Typography variant="h5" color="info.main">
                            {formatPercent(averageGrossMargin)}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Tabs */}
              <Paper sx={{ mb: 3 }}>
                <Tabs
                  value={activeTab}
                  onChange={(e, newValue) => setActiveTab(newValue)}
                  sx={{ borderBottom: 1, borderColor: "divider" }}
                >
                  <Tab label="Overview" />
                  <Tab label="Detailed Table" />
                  <Tab label="Trends" />
                </Tabs>
              </Paper>

              <AnimatePresence mode="wait">
                {activeTab === 0 && (
                  <motion.div
                    key="overview"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.4 }}
                  >
                    <Grid container spacing={3}>
                      <Grid item xs={12} lg={6}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" gutterBottom>
                              Revenue vs Net Income Trend
                            </Typography>
                            <Box sx={{ height: 400 }}>
                              <Line
                                data={lineChartData}
                                options={chartOptions}
                              />
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} lg={6}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" gutterBottom>
                              Recent Performance (Last 12 Periods)
                            </Typography>
                            <Box sx={{ height: 400 }}>
                              <Bar data={barChartData} options={chartOptions} />
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </motion.div>
                )}

                {activeTab === 1 && (
                  <motion.div
                    key="table"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.4 }}
                  >
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Detailed Profit & Loss Statement
                        </Typography>
                        <TableContainer sx={{ maxHeight: 600 }}>
                          <Table stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell align="right">Revenue</TableCell>
                                <TableCell align="right">COGS</TableCell>
                                <TableCell align="right">
                                  Gross Profit
                                </TableCell>
                                <TableCell align="right">
                                  Operating Expenses
                                </TableCell>
                                <TableCell align="right">
                                  Operating Income
                                </TableCell>
                                <TableCell align="right">
                                  Interest Expense
                                </TableCell>
                                <TableCell align="right">Taxes</TableCell>
                                <TableCell align="right">Net Income</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {profitLossData.map((row, index) => (
                                <TableRow key={index} hover>
                                  <TableCell>
                                    {excelDateToJS(row.Date)}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatCurrency(row.Revenue)}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatCurrency(row.COGS)}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Chip
                                      label={formatCurrency(
                                        row["Gross Profit"]
                                      )}
                                      color={
                                        row["Gross Profit"] >= 0
                                          ? "success"
                                          : "error"
                                      }
                                      variant="outlined"
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatCurrency(row["Operating Expenses"])}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Chip
                                      label={formatCurrency(
                                        row["Operating Income"]
                                      )}
                                      color={
                                        row["Operating Income"] >= 0
                                          ? "success"
                                          : "error"
                                      }
                                      variant="outlined"
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatCurrency(row["Interest Expense"])}
                                  </TableCell>
                                  <TableCell align="right">
                                    {formatCurrency(row.Taxes)}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Chip
                                      label={formatCurrency(row["Net Income"])}
                                      color={
                                        row["Net Income"] >= 0
                                          ? "success"
                                          : "error"
                                      }
                                      size="small"
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}

                {activeTab === 2 && (
                  <motion.div
                    key="trends"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.4 }}
                  >
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" gutterBottom>
                              Financial Performance Trends
                            </Typography>
                            <Box sx={{ height: 500 }}>
                              <Line
                                data={{
                                  labels: chartLabels,
                                  datasets: [
                                    {
                                      label: "Revenue",
                                      data: revenueData,
                                      borderColor: "#EB5A3C",
                                      backgroundColor: "rgba(235, 90, 60, 0.1)",
                                      tension: 0.4,
                                    },
                                    {
                                      label: "Gross Profit",
                                      data: grossProfitData,
                                      borderColor: "#28a745",
                                      backgroundColor: "rgba(40, 167, 69, 0.1)",
                                      tension: 0.4,
                                    },
                                    {
                                      label: "Net Income",
                                      data: netIncomeData,
                                      borderColor: "#007bff",
                                      backgroundColor: "rgba(0, 123, 255, 0.1)",
                                      tension: 0.4,
                                    },
                                  ],
                                }}
                                options={chartOptions}
                              />
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </Container>
        </Box>
      </ThemeProvider>
    </Layout>
  );
};

export default Profit_Loss;
